import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import { sanitizeUrl } from '@braintree/sanitize-url';
import styled from 'styled-components';

import { mediaQueries } from 'src/theme/constants/media';

import { createSpacingFields } from '../fields/spacing-fields';
import { destinationField } from '../fields/widgets/destination-field';
import { toggleField } from '../fields/widgets/toggle-field';
import { interpolateFromPuck } from '../interpolation';
import {
  BRAND_COLOR,
  BRAND_CONTRAST_COLOR,
  BUTTON_FONT_WEIGHT,
  BUTTON_INSET,
  BUTTON_RADIUS,
  lookup,
  NO_SPACING,
  spacingToCss,
} from '../fields/tokens';
import type { BlockWidthOption, BoxSpacing } from '../fields/tokens';

type TextAlign = 'center' | 'left' | 'right';

/**
 * Visual treatments, all derived from the merchant's configured buttons/links color so
 * a CMS button matches the rest of their storefront.
 */
const BUTTON_STYLES = {
  primary: {
    background: BRAND_COLOR,
    color: BRAND_CONTRAST_COLOR,
    border: 'none',
    textDecoration: 'none',
  },
  secondary: {
    background: 'transparent',
    color: BRAND_COLOR,
    border: `2px solid ${BRAND_COLOR}`,
    textDecoration: 'none',
  },
  tertiary: {
    background: 'transparent',
    color: BRAND_COLOR,
    border: 'none',
    textDecoration: 'underline',
  },
} as const;

type ButtonStyle = keyof typeof BUTTON_STYLES;

export type ButtonProps = {
  label: string;
  href: string;
  openInNewTab: boolean;
  buttonStyle: ButtonStyle;
  desktopWidth: BlockWidthOption;
  mobileWidth: BlockWidthOption;
  align: TextAlign;
  padding: BoxSpacing;
};

const Wrapper = styled.div`
  box-sizing: border-box;
  width: var(--cms-button-width, auto);
  text-align: var(--cms-button-align, left);
  padding: var(--cms-button-padding, 0px);

  @media ${mediaQueries.largePhone} {
    width: var(--cms-button-mobile-width, var(--cms-button-width, auto));
  }
`;

/**
 * `fill` has to apply to the anchor itself rather than the wrapper, since an inline-block
 * anchor ignores a wrapper's width. The mobile value falls back to the desktop one.
 */
const StyledButton = styled.a`
  display: inline-block;
  box-sizing: border-box;
  width: var(--cms-button-width, auto);
  padding: ${BUTTON_INSET};
  border-radius: ${BUTTON_RADIUS};
  font-size: var(--cms-button-font-size, 16px);
  font-weight: ${BUTTON_FONT_WEIGHT};
  text-align: center;
  text-decoration: var(--cms-button-text-decoration, none);
  cursor: var(--cms-button-cursor, pointer);
  background: var(--cms-button-background, ${BUTTON_STYLES.primary.background});
  color: var(--cms-button-color, ${BUTTON_STYLES.primary.color});
  border: var(--cms-button-border, none);

  &:focus-visible {
    outline: var(--dcm-cms-button-focus-outline, 3px solid var(--dcm-color-primary, #0b99a8));
    outline-offset: 2px;
  }

  @media ${mediaQueries.largePhone} {
    width: var(--cms-button-mobile-width, var(--cms-button-width, auto));
  }
`;

const SPACING_FIELDS = createSpacingFields();

/** `fill` needs a percentage on the anchor; `fit` lets content size it. */
const ANCHOR_WIDTHS = { fit: 'auto', fill: '100%' } as const;

const DESKTOP_WIDTH_OPTIONS = [
  { label: 'Fit content', value: 'fit' },
  { label: 'Full width', value: 'fill' },
];

const MOBILE_WIDTH_OPTIONS = [
  { label: 'Full width', value: 'fill' },
  { label: 'Fit content', value: 'fit' },
];

export const buttonConfig: ComponentConfig<ButtonProps> = {
  label: 'Button',
  inline: true,
  ai: {
    instructions:
      'A call-to-action link styled as a button. Use for navigation, signups, and primary actions. Color comes from the merchant theme, so pick a style rather than a color.',
  },
  fields: {
    label: { type: 'text', label: 'Label' },
    href: destinationField('Destination', {
      instructions: 'Internal storefront page or external URL for the button. Leave empty for no destination.',
    }),
    buttonStyle: {
      type: 'radio',
      label: 'Style',
      options: [
        { label: 'Primary', value: 'primary' },
        { label: 'Secondary', value: 'secondary' },
        { label: 'Text link', value: 'tertiary' },
      ],
      ai: {
        schema: { type: 'string', enum: ['primary', 'secondary', 'tertiary'] },
        instructions:
          'primary = filled with the brand color, secondary = outlined, tertiary = text only. Use one primary button per page section.',
      },
    },
    desktopWidth: {
      type: 'radio',
      label: 'Desktop width',
      options: DESKTOP_WIDTH_OPTIONS,
      ai: { schema: { type: 'string', enum: ['fit', 'fill'] } },
    },
    mobileWidth: {
      type: 'radio',
      label: 'Mobile',
      options: MOBILE_WIDTH_OPTIONS,
      ai: {
        schema: { type: 'string', enum: ['fit', 'fill'] },
        instructions: 'Full-width buttons are usually easier to tap on phones.',
      },
    },
    align: {
      type: 'radio',
      label: 'Alignment',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
      ai: { schema: { type: 'string', enum: ['left', 'center', 'right'] } },
    },
    openInNewTab: toggleField('Open in new tab'),
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: SPACING_FIELDS,
    },
  },
  resolveFields: (data, { fields }) => {
    const storedHref: unknown = data.props.href;
    let destination = '';
    if (storedHref === undefined) destination = '/';
    else if (typeof storedHref === 'string') destination = storedHref;
    const alignmentIsEffective = data.props.desktopWidth !== 'fill' || data.props.mobileWidth !== 'fill';
    const { label, href, buttonStyle, desktopWidth, mobileWidth, align, openInNewTab, padding } = fields;

    return {
      label,
      href,
      buttonStyle,
      desktopWidth,
      mobileWidth,
      ...(alignmentIsEffective ? { align } : {}),
      ...(destination.trim() !== '' ? { openInNewTab } : {}),
      padding,
    } as typeof fields;
  },
  defaultProps: {
    label: 'Click me',
    href: '/',
    openInNewTab: false,
    buttonStyle: 'primary',
    desktopWidth: 'fit',
    mobileWidth: 'fill',
    align: 'left',
    padding: NO_SPACING,
  },
  render: ({
    label = 'Click me',
    href = '/',
    openInNewTab = false,
    buttonStyle = 'primary',
    desktopWidth = 'fit',
    mobileWidth = 'fill',
    align = 'left',
    padding,
    puck,
  }) => {
    const treatment = lookup(BUTTON_STYLES, buttonStyle, BUTTON_STYLES.primary);
    // Config consumers and direct render contracts can invoke render without Puck's editor metadata.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const dragRef = puck?.dragRef;
    const resolvedLabel = interpolateFromPuck(typeof label === 'string' ? label : '', puck);
    const safeHref =
      resolvedLabel.trim() !== '' && typeof href === 'string' && href.trim() !== '' ? sanitizeUrl(href) : undefined;
    // Saved JSON is not constrained by ButtonProps; only a literal true may open another tab.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
    const newTab = safeHref !== undefined && openInNewTab === true;

    return (
      <Wrapper
        ref={dragRef}
        style={
          {
            '--cms-button-width': lookup(ANCHOR_WIDTHS, desktopWidth, ANCHOR_WIDTHS.fit),
            '--cms-button-mobile-width': lookup(ANCHOR_WIDTHS, mobileWidth, ANCHOR_WIDTHS.fill),
            '--cms-button-align': align,
            '--cms-button-padding': spacingToCss(padding),
          } as React.CSSProperties
        }
      >
        <StyledButton
          href={safeHref}
          target={newTab ? '_blank' : undefined}
          // noopener/noreferrer: a new-tab link would otherwise hand the destination a
          // window.opener handle back into the storefront.
          rel={newTab ? 'noopener noreferrer' : undefined}
          style={
            {
              '--cms-button-background': treatment.background,
              '--cms-button-color': treatment.color,
              '--cms-button-border': treatment.border,
              '--cms-button-text-decoration': treatment.textDecoration,
              '--cms-button-cursor': safeHref === undefined ? 'default' : 'pointer',
            } as React.CSSProperties
          }
        >
          {resolvedLabel}
        </StyledButton>
      </Wrapper>
    );
  },
};
